// src/pages/BlogCategoriesPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Table,
  Modal,
  Form,
  Input,
  Select,
  Button,
  Tag,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import { EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";

import {
  fetchBlogCategories,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
} from "../../../api/blogCategoryApi";
import { toastError, toastSuccess } from "../../../utils/toast";

const { Title } = Typography;

const TYPE_OPTIONS = [
  { label: "Blogs", value: "Blogs" },
  { label: "Case Studies", value: "Case Studies" },
];

export default function BlogCategoriesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // record or null
  const [form] = Form.useForm();

  // 🔎 Top-bar filters
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState(null); // "Blogs" | "Case Studies" | null

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchBlogCategories();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      toastError("Failed to load categories.");
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setModalOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      await deleteBlogCategory(record.id);
      toastSuccess("Category deleted.");
      load();
    } catch (e) {
      toastError(
        e?.response?.data?.message || e?.message || "Failed to delete category."
      );
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        title: values.title?.trim(),
        type: values.type, // "Blogs" | "Case Studies"
      };

      if (editing?.id) {
        await updateBlogCategory(editing.id, payload);
        toastSuccess("Category Updated Successfully.");
      } else {
        await createBlogCategory(payload);
        toastSuccess("Category Created Successfully.");
      }

      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      load();
    } catch (e) {
      if (!e?.errorFields) {
        toastError(
          e?.response?.data?.message || e?.message || "Save failed. Try again."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  // 🧮 Client-side filtered list (updates instantly on change)
  const filteredList = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return (list || []).filter((row) => {
      const matchesType = typeFilter ? row.type === typeFilter : true;
      const matchesSearch = q
        ? (row.title || "").toLowerCase().includes(q)
        : true;
      return matchesType && matchesSearch;
    });
  }, [list, searchText, typeFilter]);

  const columns = useMemo(
    () => [
      {
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 80,
        render: (v, _r, i) => i + 1,
      },
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        render: (text) => <strong>{text}</strong>,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 180,
        render: (v) =>
          v === "Case Studies" ? (
            <Tag color="geekblue">Case Studies</Tag>
          ) : (
            <Tag color="green">Blogs</Tag>
          ),
        // Keep built-in table filters too (optional)
        filters: TYPE_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
        onFilter: (value, record) => record.type === value,
      },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        render: (_, record) => (
          <Space>
            <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => openEdit(record)}
                          style={{ borderRadius: 4, backgroundColor: "#f0f0f0" }}
                        />
            
            <Popconfirm
              title="Delete category?"
              description={`Are you sure you want to delete "${record.title}"?`}
              okText="Yes, delete"
              cancelText="Cancel"
              
              onConfirm={() => handleDelete(record)}
            >
              <Button danger shape="circle" icon={<DeleteOutlined />} style={{ borderRadius: 4, backgroundColor: "#f0f0f0" }} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    []
  );

  const clearFilters = () => {
    setSearchText("");
    setTypeFilter(null);
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Blog Categories
        </Title>
        <Button type="primary" onClick={openCreate}>
          + New Category
        </Button>
      </Space>

      {/* Top-bar Filters */}
      <Space wrap style={{ width: "100%", marginBottom: 16 }}>
        <Input
          placeholder="Search by title..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 280 }}
        />

        <Select
          allowClear
          placeholder="Filter by type"
          value={typeFilter}
          onChange={(v) => setTypeFilter(v || null)}
          options={TYPE_OPTIONS}
          style={{ width: 220 }}
        />

        <Button icon={<ReloadOutlined />} onClick={clearFilters}>
          Reset
        </Button>
      </Space>

      {/* Table */}
      <Table
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={filteredList}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editing ? "Edit Category" : "Create Category"}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        onOk={onSubmit}
        okText={editing ? "Update" : "Create"}
        confirmLoading={saving}
        destroyOnClose={false} // keep mounted so setFieldsValue always hits
        forceRender              // pre-render children to avoid timing issues
        afterOpenChange={(open) => {
          if (open) {
            if (editing) {
              form.setFieldsValue({
                title: editing.title ?? "",
                type: editing.type ?? "Blogs",
              });
            } else {
              form.setFieldsValue({ title: "", type: "Blogs" });
            }
          }
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Title"
            name="title"
            rules={[
              { required: true, message: "Title is required." },
              { min: 2, message: "Title must be at least 2 characters." },
            ]}
          >
            <Input placeholder="Enter category title" />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: "Please select a type." }]}
          >
            <Select placeholder="Select type" options={TYPE_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
